import type { StorybookConfig } from '@storybook/react-vite'

// Questi id numerici arrivano da netsuite e sono usati per identificare le stagioni dei prodotti
export const seasonsMap = {
  SPRING_SUMMER: 1,
  ALL_SEASONS: 2,
  FALL_WINTER: 3,
  THREE_SEASONS: 4,
} as const

export const notifyMeSeasonSummerStartDate = '2017-01-01'
export const notifyMeSeasonSummerEndDate = '2017-06-30'
export const notifyMeSeasonWinterStartDate = '2017-07-01'
export const notifyMeSeasonWinterEndDate = '2017-12-31'

export const instagramCardsAmount = 12

export const fakePixelImage =
  'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=='

export const addToCartStatusChangeMilliseconds = 2500

export /**
 * 9/16 = 0.5625.
 */
const portraitAspectRatio = 0.5625

export const promotionalBannerVariants = {
  PRIMARY: 'primary',
  SEMI_TRANSPARENT: 'semiTransparent',
} as const

export const addressFields = [
  'address1',
  'address2',
  'zip',
  'city',
  'province',
  'country',
  'phone',
] as const

export const defaultBottegaDetails = {
  title: '',
  subtitle: '',
  form_title: '',
  form_placeholder: '',
  pretitle: '',
  title_02: '',
  paragraph: '',
  label_read_more: '',
  hours_html: '',
  label_hours: '',
  label_mon_fri: '',
  label_sat: '',
  label_sun: '',
  label_address: '',
  label_nearest_bottega: '',
} as const

export const defaultCityIcons = {
  title: '',
  subtitle: '',
  form_title: '',
  form_placeholder: '',
  pretitle: '',
  title_02: '',
  paragraph: '',
  label_read_more: '',
  hours_html: '',
  label_hours: '',
  label_mon_fri: '',
  label_sat: '',
  label_sun: '',
  label_address: '',
} as const

export const defaultSettings = {
  language: 'it',
  collection_commons_sole_in: 'Suola in',
  cross_currency: '€',
  currency: 'EUR',
  reviews_label: 'Recensioni',
  store_nation: 'it',
  ns_lang_value: '1',
  locale: 'it-IT',
  cart_delete: 'Elimina',
  imageAssetPublicPath: '//velasca-beta.myshopify.com/cdn/shop/files/',
  currency_format: 'money_with_currency_format',
  money_format: '€ {{amount}}',
  money_with_currency_format: 'EUR {{amount}}',
} as const

export const betaGraphqlSchemaUri =
  'https://velasca-beta.myshopify.com/api/2024-07/graphql.json'

export const storybookConfigBase: StorybookConfig = {
  stories: ['../src/**/*.stories.@(js|jsx|ts|tsx)'],
  addons: [
    '@storybook/addon-links',
    '@storybook/addon-essentials',
    '@storybook/addon-interactions',
  ],
  framework: {
    name: '@storybook/react-vite',
    options: {},
  },
  docs: {
    autodocs: 'tag',
  },
  staticDirs: ['./theme_assets'],
  typescript: {
    reactDocgen: false,
  },
}

export const brands = ['candiani', 'attanasio'] as const

interface BrandTheme {
  colors: {
    primary: string
    secondary: string
    primaryLight: string
    hover: string
    background: string
    boxBackground: string
    modalBackground: string
    backgroundOverlay: string
    disabled: string
    sliderBackground: string
    carouselBackground: string
    sliderActionButtonActive: string
    sliderActionButtonInactive: string
    footerBackground: string
    danger: string
    brandFrame: string
    border: string
    footerSeparator: string
    modalShadow: string
    skeleton: {
      baseColor: string
      highlightColor: string
    }
    text: {
      primary: string
      light: string
      secondary: string
    }
  }
}

const colorsPalette = {
  transparent: 'rgba(0, 0, 0, 0)',
  semiTransparent: 'rgba(49, 51, 50, 0.6);',
  darkTransparent: '#000000a6',
  pureWhite: '#FFFFFF',
  light: '#FFFEFA',
  dark: '#313332',
  brownSugar500: '#B0684F',
  bole500: '#854E41',
  metal500: '#8B959A',
  metal400: '#C2C3C3',
  metal300: '#D9D9D9',
  metal200: '#EEEEEE',
  metal100: '#F9F8F7',
  ocean500: '#185674',
  ocean400: '#427892',
  // ocean300: '#7691A3', unused
  emerald500: '#23595B',
  emerald400: '#457B7D',
  // emerald200: '#DAE0E0', unused
  sand500: '#A39062',
  sand400: '#CABC9B',
  sand300: '#DBD4C3',
  sand200: '#EAE5DC',
  sand100: '#F3EFE7',
  navy500: '#385181',
  navy300: '#6C7EA1',
  // navy200: '#DEE3EC', unused
  cloud500: '#8596B0',
  cloud400: '#A5B1C3',
  cloud300: '#C2C8D1',
  cloud200: '#E2E5EA',
  cloud100: '#F3F4F4',
  // indigo500: '#242959', unused
  // indigo300: '#767994', unused
  // indigo200: '#D1D1D9', unused
  // mud500: '#AD8E71', unused
  // mud400: '#C9B29D', unused
  // mud300: '#D9CCC0', unused
  // mud200: '#EEE4DB', unused
  // mud100: '#F6F4F1', unused
  military500: '#4B6C5E',
  military400: '#628173',
  military300: '#BAC3B8',
  military100: '#F8F6F0',
}

const velascaManTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.ocean500,
    secondary: colorsPalette.metal300,
    primaryLight: colorsPalette.ocean400,
    hover: colorsPalette.metal500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.metal100,
    modalBackground: colorsPalette.metal200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.metal100,
    sliderActionButtonActive: colorsPalette.metal500,
    sliderActionButtonInactive: colorsPalette.metal300,
    carouselBackground: colorsPalette.metal200,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.metal400,
    danger: colorsPalette.brownSugar500,
    border: colorsPalette.metal400,
    brandFrame: colorsPalette.ocean500,
    footerSeparator: colorsPalette.metal500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.metal300,
      highlightColor: colorsPalette.metal200,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.metal500,
    },
  },
}

const velascaWomanTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.military500,
    secondary: colorsPalette.metal300,
    primaryLight: colorsPalette.military400,
    hover: colorsPalette.metal500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.military100,
    modalBackground: colorsPalette.metal100,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.military100,
    sliderActionButtonActive: colorsPalette.military500,
    sliderActionButtonInactive: colorsPalette.military300,
    carouselBackground: colorsPalette.metal100,
    footerBackground: colorsPalette.military500,
    disabled: colorsPalette.metal400,
    danger: colorsPalette.bole500,
    border: colorsPalette.metal400,
    brandFrame: colorsPalette.ocean500,
    footerSeparator: colorsPalette.military300,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.metal300,
      highlightColor: colorsPalette.metal200,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.metal500,
    },
  },
}

const attanasioTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.emerald500,
    secondary: colorsPalette.sand300,
    primaryLight: colorsPalette.emerald400,
    hover: colorsPalette.sand500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.sand100,
    modalBackground: colorsPalette.sand200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.pureWhite,
    carouselBackground: colorsPalette.transparent,
    sliderActionButtonActive: colorsPalette.military500,
    sliderActionButtonInactive: colorsPalette.military300,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.sand400,
    danger: colorsPalette.brownSugar500,
    brandFrame: colorsPalette.sand400,
    border: colorsPalette.sand400,
    footerSeparator: colorsPalette.metal500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.sand300,
      highlightColor: colorsPalette.sand200,
    },
    text: {
      primary: colorsPalette.dark,
      light: colorsPalette.sand200,
      secondary: colorsPalette.light,
    },
  },
}

const candianiTheme: BrandTheme = {
  colors: {
    primary: colorsPalette.navy500,
    secondary: colorsPalette.cloud300,
    primaryLight: colorsPalette.navy300,
    hover: colorsPalette.cloud500,
    background: colorsPalette.pureWhite,
    boxBackground: colorsPalette.cloud100,
    modalBackground: colorsPalette.cloud200,
    backgroundOverlay: colorsPalette.semiTransparent,
    sliderBackground: colorsPalette.cloud100,
    sliderActionButtonActive: colorsPalette.cloud500,
    sliderActionButtonInactive: colorsPalette.cloud300,
    carouselBackground: colorsPalette.cloud200,
    footerBackground: colorsPalette.dark,
    disabled: colorsPalette.cloud300,
    danger: colorsPalette.brownSugar500,
    border: colorsPalette.cloud400,
    brandFrame: colorsPalette.navy500,
    footerSeparator: colorsPalette.cloud500,
    modalShadow: colorsPalette.darkTransparent,
    skeleton: {
      baseColor: colorsPalette.cloud200,
      highlightColor: colorsPalette.cloud300,
    },
    text: {
      primary: colorsPalette.dark,
      secondary: colorsPalette.pureWhite,
      light: colorsPalette.cloud500,
    },
  },
}

export const designSystemTheme = {
  man: {
    ...velascaManTheme,
    logo: {
      primary: colorsPalette.ocean500,
      white: colorsPalette.pureWhite,
    },
    fonts: {
      brand: "'Jost', serif;",
      serif: {
        fontFamily: "'Jomolhari', serif;",
      },
    },
    brand: {
      velasca: velascaManTheme,
      attanasio: attanasioTheme,
      candiani: candianiTheme,
    },
  },
  woman: {
    ...velascaWomanTheme,
    logo: {
      primary: colorsPalette.military500,
      white: colorsPalette.pureWhite,
    },
    fonts: {
      brand: "'Jost', serif;",
      serif: {
        fontFamily: "'Jomolhari', serif;",
      },
    },
    brand: {
      velasca: velascaWomanTheme,
      attanasio: attanasioTheme,
      candiani: candianiTheme,
    },
  },
}

export const featureFlagsCatalog = {
  /**
   * Key of the PostHog feature flag.
   */
  COLLECTION_PAGE_CARDS: 'collection-page-indossate-experiment',
  /**
   * Key of the PostHog feature flag.
   */
  FILTERS: 'collection-page-filters-experiment',
} as const

export const notificationMessageTypePerSeason = {
  seasonWinter: 'season-winter',
  seasonSummer: 'season-summer',
  discontinued: 'limited',
} as const

export const dataTestIds = {
  SHOP_THE_LOOK_BUTTON: 'shop-the-look-button',
  ADD_TO_CART_TEXT: 'add-to-cart-text',
  MENU_LINKS: 'menu-links',
  PRODUCT_PRICE_IN_CARD: 'product-price-in-card',
  RECENT_PRODUCTS_SLIDER: 'recent-products-slider',
  COMPLEMENTARY_PRODUCTS_SLIDER: 'complementary-products-slider',
  SIMILAR_PRODUCTS_SLIDER: 'similar-products-slider',
  GENERIC_PRODUCTS_SLIDER: 'generic-products-slider',
  QUICK_ADD_TO_CART_BUTTON: 'quick-add-to-cart-button',
  CART_ICON: 'cart-icon',
  SIZE_RADIO_BUTTON: 'size-radio-button',
  QUICK_ADD_TO_CART_LINE_ITEM: 'quick-add-to-cart-line-item',
  HOMEPAGE_HERO_CTA: 'homepage-hero-cta',
  MOBILE_SUGGESTED_PRODUCTS: 'mobile-suggested-products',
  CART_PRODUCT_LIST: 'cart-product-list',
  LINE_ITEM: 'line-item',
} as const

export const fallbackStore = 'row'

export const supportedIsocodesByStore = {
  it: ['IT', 'SM', 'VA'],
  eu: [
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'EE',
    'FI',
    'DE',
    'GR',
    'HU',
    'IE',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'PL',
  ],
  dk: ['DK'],
  uk: ['GB'],
  fr: ['FR', 'MC'],
  us: ['US'],
}

export const velascaApi = {
  production: {
    MAN: 'https://api.velasca.com',
    WOMAN: 'https://api.velascawomen.com',
  },
  staging: {
    MAN: 'https://sg.api.velasca.com',
    WOMAN: 'https://sg.api.velascawomen.com',
  },
} as const
